import { useState } from "react";
import { motion } from "framer-motion";
import { trackBoth } from "@/lib/meta";

const API = process.env.REACT_APP_BACKEND_URL;

// Shown when a shopper picks a colourway that isn't in the current drop
// (Pink and Black are orderable). Collects an email so they can be notified
// when the colour drops, and nudges them back to a preorderable pair.
export default function ColourDropBanner({ colour, onShopPink }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | done | error
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const clean = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      setState("error");
      setMsg("Enter a valid email address.");
      return;
    }
    setState("sending");
    setMsg("");
    try {
      const r = await fetch(`${API}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: clean,
          source: "pdp-colour-notify",
          // Canonical Klaviyo colour value (matches the waitlist funnel).
          colour: colour?.klaviyo || colour?.name,
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setState("done");
      trackBoth("Lead", { content_name: `colour-notify-${colour?.id}` });
    } catch {
      setState("error");
      setMsg("Something went wrong — try again.");
    }
  };

  return (
    <motion.div
      id="colour-drop-banner"
      data-testid="colour-drop-banner"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 16 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="mt-6 rounded-3xl border-2 border-ink p-5"
      style={{ backgroundColor: `${colour?.hex || "#FFD84D"}33` }}
    >
      <p className="font-body font-bold text-[11px] tracking-[0.22em] uppercase text-ink/50">
        Not in this drop
      </p>
      <h3
        data-testid="drop-banner-title"
        className="mt-1.5 font-display uppercase leading-tight text-2xl tracking-wide"
      >
        The {colour?.name} hasn't dropped yet.
      </h3>
      <p className="mt-2 text-sm text-ink/70 font-medium leading-snug">
        Pink and Black are the colourways in this drop. Pop your email in and
        we'll let you know the second {colour?.name} lands.
      </p>

      {state === "done" ? (
        <p
          data-testid="drop-banner-success"
          className="mt-4 rounded-2xl border-2 border-ink bg-ivory px-4 py-3 text-sm font-semibold"
        >
          You're on the list for {colour?.name} — we'll email you the moment it
          drops.
        </p>
      ) : (
        <form onSubmit={submit} className="mt-4">
          <div className="flex gap-2">
            <input
              id="drop-notify-email"
              data-testid="drop-notify-email"
              data-clarity-mask="true"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="flex-1 min-w-0 min-h-[48px] rounded-full border-2 border-ink bg-ivory px-4 text-sm font-medium placeholder:text-ink/35 focus:outline-none"
            />
            <button
              data-testid="drop-notify-submit"
              type="submit"
              disabled={state === "sending"}
              className="shrink-0 min-h-[48px] px-5 rounded-full bg-ink text-ivory font-display uppercase text-sm tracking-wide transition-transform duration-150 active:scale-95 disabled:opacity-60"
            >
              {state === "sending" ? "…" : "Notify me"}
            </button>
          </div>
          {msg && (
            <p
              data-testid="drop-banner-error"
              role="alert"
              className="mt-2 text-sm font-medium text-[#C2271B]"
            >
              {msg}
            </p>
          )}
        </form>
      )}

      <div className="mt-4 pt-4 border-t-2 border-ink/10">
        <button
          data-testid="drop-shop-pink"
          onClick={onShopPink}
          className="w-full min-h-[48px] rounded-full bg-[#FF6BAA] text-ink font-display uppercase text-sm tracking-wide border-2 border-ink shadow-[2px_2px_0px_#111111] transition-transform duration-150 active:scale-[0.98]"
        >
          Or get the Pink or Black pair — they're in this drop
        </button>
      </div>
    </motion.div>
  );
}
